import { Link } from "react-router-dom";

const sections = [
  {
    title: "Who we are",
    body: (
      <>
        Neurological Communication Partners is a private speech &amp; language
        therapy practice operated by Kathleen McGrath, M.S. CCC-SLP, licensed
        in New Hampshire and Vermont (ASHA CCC-SLP) and Ireland (CORU). We act as a{" "}
        <strong>covered entity</strong> under HIPAA and a{" "}
        <strong>data controller</strong> under the GDPR for personal data
        collected through this website and during therapy.
      </>
    ),
  },
  {
    title: "What we collect",
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          Contact &amp; appointment details you provide (name, email, phone,
          region, brief note).
        </li>
        <li>
          Clinical information necessary for assessment and therapy
          (Protected Health Information / PHI).
        </li>
        <li>
          Session-join metadata: your initials, the meeting reference, a
          one-way hashed IP, and your browser&rsquo;s user agent — used as an
          audit trail of consent.
        </li>
      </ul>
    ),
  },
  {
    title: "How we use it",
    body: (
      <>
        Information is used only to schedule, deliver and document therapy.
        We do <strong>not</strong> sell, rent or use your data for marketing
        or analytics.
      </>
    ),
  },
  {
    title: "Telehealth platform",
    body: (
      <>
        Live sessions are delivered via{" "}
        <strong>Zoom for Healthcare</strong>, a HIPAA-eligible service operated
        under a Business Associate Agreement. Sessions are encrypted in transit
        and are <em>not</em> recorded unless you provide explicit verbal
        consent during the session.
      </>
    ),
  },
  {
    title: "Legal bases (GDPR)",
    body: (
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong>Consent</strong> — for processing health data and contacting
          you about scheduling.
        </li>
        <li>
          <strong>Contract</strong> — to provide the therapy service you
          request.
        </li>
        <li>
          <strong>Legitimate interest</strong> — security, fraud prevention,
          and minimal audit logging.
        </li>
      </ul>
    ),
  },
  {
    title: "Your rights",
    body: (
      <>
        You may request access, correction, deletion, restriction or
        portability of your data, withdraw consent at any time, and lodge a
        complaint with the Irish Data Protection Commission (Ireland) or the
        U.S. Department of Health &amp; Human Services Office for Civil Rights
        (USA). To exercise any right, email{" "}
        <a
          className="underline text-[#739072]"
          href="mailto:neurocompartners@gmail.com"
        >
          neurocompartners@gmail.com
        </a>
        .
      </>
    ),
  },
  {
    title: "Retention",
    body: (
      <>
        Clinical records are retained per applicable U.S. state and Irish
        professional requirements (typically 7 years; longer for minors).
        Website inquiry data is kept for 24 months and then deleted.
      </>
    ),
  },
  {
    title: "Security",
    body: (
      <>
        We apply administrative, technical and physical safeguards including
        encryption in transit (TLS), least-privilege access, and minimum
        necessary data collection. No internet system is perfectly secure —
        please do not transmit sensitive health information through the public
        contact form.
      </>
    ),
  },
];

export default function PrivacyNotice() {
  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#2D332F]">
      <header className="border-b border-black/5 bg-[#FDFCF8]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="privacy-back-home"
            className="flex items-center"
          >
            <img
              src="/logo.jpg"
              alt="Neurological Communication Partners"
              className="h-14 w-auto rounded-xl ring-1 ring-black/5"
            />
          </Link>
          <Link
            to="/"
            className="text-sm text-[#5C6661] hover:text-[#2D332F] transition"
          >
            ← Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
          Privacy Notice
        </p>
        <h1
          data-testid="privacy-title"
          className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-[1.05]"
        >
          How we handle your information.
        </h1>
        <p className="mt-4 text-[#5C6661]">
          Effective {new Date().toLocaleDateString("en-IE", { year: "numeric", month: "long" })}
          . Aligned with HIPAA (USA) &amp; GDPR (EU/Ireland).
        </p>

        <div className="mt-12 space-y-12">
          {sections.map((s, i) => (
            <section
              key={s.title}
              data-testid={`privacy-section-${i}`}
              className="border-t border-black/10 pt-8"
            >
              <h2 className="font-serif text-2xl md:text-3xl text-[#2D332F] mb-4">
                {String(i + 1).padStart(2, "0")}. {s.title}
              </h2>
              <div className="text-[#2D332F]/85 leading-relaxed text-base md:text-lg">
                {s.body}
              </div>
            </section>
          ))}
        </div>

        <p className="mt-16 text-sm text-[#5C6661]">
          This notice is provided for transparency. It is not legal advice.
          Kathleen will provide a fuller Notice of Privacy Practices and
          consent forms before therapy begins.
        </p>
      </main>
    </div>
  );
}
